import { useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { fromCallback, fromPromise } from 'xstate';

import {
  AppointmentRecurring, CareType, getAllStates, getFeatureFlags, getMatchPreference, getOnboardingInviteToken, getPatientDetails, getProviderProfileFromUrl,
  getSupportedAgeGroups, getTherapyTypes, getUserMatches, getUserProfile,
  InsuranceMetaGrouping, Match, MatchPreference, patientCheckInviteToken,
  PatientDetails
} from '@uplift/api';
import { PATIENT_ONBOARDING_ROUTING_CONFIG } from '@uplift/constants';
import { useCurrentuser, useSetTimezone } from '@uplift/contexts';
import { useStore } from '@uplift/hooks';
import { useInsuranceCompaniesQuery, useModalitiesQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { ProviderFilterTypeEnum, ProviderMedicalRole } from '@uplift/types';
import { useMachine } from '@xstate/react';

import { captureUTMParamsIntoCookie, checkUTMParamsIsBetterhelpUser, getAllUTMParams, getBookingForEnumFromModality, getModalityFromBookingForEnum } from '../../utils';
import { LoadActorReturnType, MatchingFlowTypeEnum, OnboardingMachineContext } from '../types';
import { onboardingMachine } from './machine';
import { BookingForEnum } from '../../types';
import { AuthStore } from '@uplift/store';
import { shouldShowWelcomeStepAB } from './utils';

type Params = {
  invite_token?: string;
} | {
  profile_data?: string;
};

type ProfileData = {
  providerUrl: string;
  /**
   * this is the appointment start date time in ISO format
   */
  appointmentStartDateTime: string;
  timezone: string;
  referrer?: string;
};

type UseOnboardingMachineProps = {
  isExiting: boolean;
};

const getMoreCurrentUserData = async (userId: string, isLoggedIn: boolean): Promise<{
  match: Match;
  matchPreference: MatchPreference;
  patientDetails: PatientDetails;
  contextData: Partial<OnboardingMachineContext>;
}> => {
  if (!isLoggedIn) {
    return {
      match: null,
      matchPreference: null,
      patientDetails: null,
      contextData: {},
    };
  }

  const matches = await getUserMatches({ userId, states: [ 'initial', 'active' ], forceMatchCreateIfMissing: true });
  const initialMatch = matches.data.matches[0];
  const matchPreferences = await getMatchPreference(initialMatch.id);

  const modalitiesResponse = await getTherapyTypes();
  const modality = modalitiesResponse.therapyTypes.find(m => m.id === matchPreferences.data.modalityId);
  const bookingFor = modality ? getBookingForEnumFromModality(modality) : null;

  const selectedFilters: ProviderFilterTypeEnum[] = [];

  if (
    matchPreferences.data.faithIds?.length
    || matchPreferences.data.ethnicityIds?.length
    || matchPreferences.data.genderIds?.length
  ) {
    selectedFilters.push(ProviderFilterTypeEnum.Identity);
  }

  if (
    matchPreferences.data.communityExpertiseFaithIds?.length
    || matchPreferences.data.communityExpertiseEthnicityIds?.length
    || matchPreferences.data.communityExpertiseGeneralIds?.length
  ) {
    selectedFilters.push(ProviderFilterTypeEnum.Experience);
  }

  const ageGroupsResponse = await getSupportedAgeGroups();
  const ageGroup = ageGroupsResponse.ages.find(a => a.id === matchPreferences.data.ageGroupId);

  const patientDetails = await getPatientDetails(userId);
  const careType = patientDetails.data?.onboardingCaretypeSelection ?? CareType.therapy;
  const recommendedCareType = patientDetails.data?.onboardingRecommendedCaretype ?? CareType.therapy;

  const contextData: Partial<OnboardingMachineContext> = {
    matchId: initialMatch.id,
    userId: userId,
    careType: careType,
    recommendedCareType: recommendedCareType,
    seed: matchPreferences.data.seed,
    stateId: matchPreferences.data.locationState,
    specialtyIds: matchPreferences.data.visitReasonIds ?? [],
    insuranceId: matchPreferences.data.insuranceMatch,
    modalityId: matchPreferences.data.modalityId,
    who: bookingFor,
    age: ageGroup ? { id: ageGroup.id, ageGroup: ageGroup.ageGroup } : null,
    identityFilters: {
      faithIds: matchPreferences.data.faithIds,
      ethnicityIds: matchPreferences.data.ethnicityIds,
      genderIds: matchPreferences.data.genderIds,
    },
    experienceFilters: {
      faithIds: matchPreferences.data.communityExpertiseFaithIds,
      ethnicityIds: matchPreferences.data.communityExpertiseEthnicityIds,
      communityIds: matchPreferences.data.communityExpertiseGeneralIds,
    },
    showTheseProviderFilters: selectedFilters,
  };

  return {
    match: initialMatch,
    matchPreference: matchPreferences.data,
    patientDetails: patientDetails.data,
    contextData: contextData,
  };
};

const getDirectFlowData = (params: Record<string, string>, matchPreference?: MatchPreference, timezone?: string): ProfileData => {
  if ('profile_data' in params) {
    const profileData = JSON.parse(atob(params.profile_data));

    return {
      appointmentStartDateTime: profileData?.appointment,
      providerUrl: profileData?.providerUrl,
      timezone: profileData?.timezone,
      referrer: profileData?.referrer,
    };
  }

  if (matchPreference?.directFlowData?.providerId) {
    return {
      appointmentStartDateTime: matchPreference.directFlowData.appointment,
      providerUrl: matchPreference.directFlowData.publicProfileUrl,
      timezone: timezone,
      referrer: null,
    };
  }

  return null;
};

const shouldSkipSnapshot = (params: Params, authStore: AuthStore) => {
  if ('invite_token' in params || 'profile_data' in params || authStore.isLoggedIn || authStore.isRedirectingAfterLogout) {
    return true;
  }

  if (checkUTMParamsIsBetterhelpUser(getAllUTMParams())) {
    return true;
  }

  return false;
};

export const useOnboardingMachine = (props: UseOnboardingMachineProps) => {
  const { data: companies = [] } = useInsuranceCompaniesQuery();
  const { authStore } = useStore();
  const me = useCurrentuser();
  const setTimezone = useSetTimezone();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(searchParams.entries()) as Params;
  const { data: modalities = [] } = useModalitiesQuery();
  const getModalityId = (who: BookingForEnum) => {
    const modalityEnum = getModalityFromBookingForEnum(who);

    return modalities.find(modality => modality.modality === modalityEnum)?.id;
  };

  const skipSnapshot = shouldSkipSnapshot(params, authStore);
  const snapshot = useMemo(() => skipSnapshot ? null : JSON.parse(localStorage.getItem('matching')), [ skipSnapshot ]);

  const machine = useMachine(onboardingMachine.provide({
    actors: {
      load: fromPromise(async (): Promise<LoadActorReturnType> => {
        const { match, matchPreference, patientDetails, contextData } = await getMoreCurrentUserData(me.id, authStore.isLoggedIn);
        const userInviteDetails = authStore.isLoggedIn ? await getOnboardingInviteToken(me.id) : null;
        const userProfile = authStore.isLoggedIn ? await getUserProfile(me.id) : null;
        const urlParams = new URLSearchParams(window.location.search);
        const preselectedStateShortName = urlParams.get('state')?.toLowerCase();
        const preselectedState = preselectedStateShortName
          ? await getAllStates().then(r => r.data.find(s => s.available && s.shortName.toLowerCase() === preselectedStateShortName.toLowerCase()))
          : null;
        const utmParams = captureUTMParamsIntoCookie();
        const isBetterhelpUser = patientDetails
          ? patientDetails.isBetterhelpUser
          : checkUTMParamsIsBetterhelpUser(utmParams);

        /**
         * Invite flow
         */
        if ('invite_token' in params || (userInviteDetails?.data.inviteToken && match.providerId)) {
          const inviteToken = 'invite_token' in params ? params.invite_token : userInviteDetails?.data.inviteToken;
          const invitation = await patientCheckInviteToken(inviteToken);

          if (invitation.error === 'invalid_invite_data') {
            // do nothing, it will fallback to matching flow
          } else if (invitation.error) {
            throw new Error(invitation.error);
          } else {
            history.replace(PATIENT_ONBOARDING_ROUTING_CONFIG.where);

            return {
              ...contextData,
              isLoggedIn: authStore.isLoggedIn,
              matchId: invitation.matchId,
              matchingFlow: MatchingFlowTypeEnum.Invite,
              selectedProviderId: invitation.providerId,
              appointment: invitation.appointment,
              inviteToken: inviteToken,
              isBetterhelpUser: isBetterhelpUser,
            };
          }
        }

        /**
         * Direct flow
         */
        const directFlowData = getDirectFlowData(params, matchPreference, userProfile?.data?.timezone);

        if (directFlowData) {
          const { providerUrl, appointmentStartDateTime, timezone, referrer } = directFlowData;

          try {
            const { data } = await getProviderProfileFromUrl(new URL(providerUrl).pathname).catch(_err => ({ data: null }));

            if (!data) {
              throw new Error('Unknown direct flow url');
            }

            const providerId = data.user.id;

            if (!providerUrl || !appointmentStartDateTime) {
              throw new Error('Unable to load direct flow data');
            }

            if (timezone) {
              setTimezone(timezone);
            }

            history.replace(PATIENT_ONBOARDING_ROUTING_CONFIG.where);

            return {
              ...contextData,
              stateId: preselectedState?.id ?? contextData.stateId,
              isBetterhelpUser: isBetterhelpUser,
              isLoggedIn: authStore.isLoggedIn,
              matchingFlow: MatchingFlowTypeEnum.Direct,
              selectedProviderId: providerId,
              providerUrl: providerUrl,
              appointment: {
                timezone: timezone,
                fromDatetime: appointmentStartDateTime,
                toDatetime: CalendarDateService.date(appointmentStartDateTime).add(60, 'minutes').toISOString(),
                recurring: {
                  [ProviderMedicalRole.therapist]: AppointmentRecurring.weekly,
                  [ProviderMedicalRole.psychiatrist]: AppointmentRecurring.none,
                }[data.user.medicalRole],
              },
              referrer: referrer
            };
          } catch (err) {
            // do nothing, it will fallback to matching flow
            console.error(err);
          }
        }

        const featureFlags = await getFeatureFlags(false);
        const isWelcomeStepABTestEnabled = featureFlags.data.ONBOARDING_WELCOME_STEP_AB_TEST;
        const _shouldShowWelcomeStepAB = isWelcomeStepABTestEnabled ? shouldShowWelcomeStepAB() : true;

        return {
          ...contextData,
          stateId: preselectedState?.id ?? contextData.stateId,
          isLoggedIn: authStore.isLoggedIn,
          matchingFlow: MatchingFlowTypeEnum.Marketplace,
          // if feature flag is enabled, use function to determine if welcome step should be shown
          // otherwise, always show welcome step by default
          // shouldShowWelcomeStep: isWelcomeStepABTestEnabled ? shouldShowWelcomeStepAB() : true,
          shouldShowWelcomeStep: preselectedState?.id ? false : _shouldShowWelcomeStepAB,
          isBetterhelpUser: isBetterhelpUser,
        };
      }),
      autoCalculateWho: fromPromise(async (): Promise<{ who: BookingForEnum; modalityId: string }> => {
        return {
          who: BookingForEnum.Myself,
          modalityId: getModalityId(BookingForEnum.Myself),
        };
      }),
      handleHistoryPop: fromCallback(({ sendBack }) => {
        history.listen(() => {
          if (history.action === 'POP') {
            sendBack({ type: 'Back' });
          }
        });
      }),
      syncRouteActor: fromCallback(({ input }) => {
        if (input.replace) {
          history.replace(input.route);
          return;
        }

        history.push(input.route);
      })
    },
    guards: {
      isInsuranceMedicaid: ({ context, event }) => {
        let insuranceId = context.insuranceId;

        if (event.type === 'InsuranceInfo.Next' || event.type === 'Insurance.Next' || event.type === 'Matches.UpdateFilters') {
          insuranceId = event.data.insuranceId;
        }

        const insurance = companies.find(c => c.id === insuranceId);

        return insurance?.metaGrouping === InsuranceMetaGrouping.medicaid;
      }
    }
  }), {
    snapshot: snapshot,
  });

  const [ state, send, actor ] = machine;
  const persistedState = actor.getPersistedSnapshot();

  useEffect(() => {
    if (props.isExiting) {
      return;
    }

    if (persistedState && !skipSnapshot) {
      localStorage.setItem('matching', JSON.stringify(persistedState));
    } else if (skipSnapshot) {
      localStorage.removeItem('matching');
    }
  }, [ persistedState, props.isExiting, skipSnapshot ]);

  const triggeredRef = useRef(false);

  useEffect(() => {
    if (triggeredRef.current) {
      return;
    }

    if (!authStore.loading && !me.isLoading) {
      triggeredRef.current = true;
      send({ type: 'Start' });
    }
  }, [ authStore.loading, me.isLoading ]);

  return { state, send };
};
